/**
 * Renders a checklist instance for completion as the chat-input replacement
 * on a claimed user task whose `tri:uxElement === 'checklist'`. Matches the
 * field-shape conventions used by the standalone ChecklistInstanceForm in
 * settings/ProcessPanel:
 *
 *   - `checklist.fields` is the structural tree (label/type + nested
 *     containers via `children`/`items`/`elements`).
 *   - `checklist.data[flatIndex]` is the flat value array, indexed by the
 *     pre-order walk of leaf-and-container rows (matches the editor's
 *     `flattenInstanceFields`).
 *   - Field label is `field.text`.
 *
 * Reports completion + the latest mutated checklist upward via `onChange`.
 * The parent uses the completion bit to gate the Send button and the
 * mutated checklist to PATCH the server-side instance.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';

// ---- field-shape helpers (mirrors settings/ProcessPanel.tsx) ----

// Mirror of settings/ProcessPanel.tsx:423. The *Capture types are containers
// (they hold child fields), not leaves — treating them as leaves swallows
// every child field. Keep this list in sync with the editor.
const CONTAINER_TYPES = [
    'buildingCapture',
    'floorCapture',
    'roomCapture',
    'assetCapture',
    'loop',
    'group',
];
const CONTAINER_ALIASES: Record<string, string> = {
    repeat: 'loop',
    foreach: 'loop',
    iteration: 'loop',
    iter: 'loop',
};
const SUPPORTED_LEAF_TYPES = new Set([
    'text',
    'string',
    'integer',
    'decimal',
    'oknotok',
    'donenotdone',
    'rating',
    'signature',
]);

function normalizeFieldType(type: string | undefined): string | undefined {
    if (type == null || type === '') return undefined;
    return String(type).toLowerCase();
}

function canonicalContainerType(type: string | undefined): string | undefined {
    if (!type) return undefined;
    const s = String(type);
    if (CONTAINER_TYPES.includes(s)) return s;
    const lower = s.toLowerCase();
    const found = CONTAINER_TYPES.find((c) => c.toLowerCase() === lower);
    if (found) return found;
    return CONTAINER_ALIASES[lower];
}

function fieldKind(f: any): string | undefined {
    if (f == null || typeof f !== 'object') return undefined;
    const t = f.type != null && f.type !== '' ? String(f.type) : undefined;
    const ft = f.fieldType != null && f.fieldType !== '' ? String(f.fieldType) : undefined;
    const canonT = canonicalContainerType(t);
    const canonFt = canonicalContainerType(ft);
    if (canonFt !== undefined && canonT === undefined) return ft;
    if (canonT !== undefined) return t;
    return t ?? ft;
}

function isContainerField(f: any): boolean {
    return canonicalContainerType(fieldKind(f)) !== undefined;
}

function childrenOfField(f: any): any[] {
    if (!f) return [];
    const c = f.children;
    const it = f.items;
    const isContainer = isContainerField(f);
    const fd = isContainer && Array.isArray(f.fields) ? f.fields : undefined;
    const el = isContainer && Array.isArray(f.elements) ? f.elements : undefined;
    const nonEmpty = [c, it, fd, el].find((a) => Array.isArray(a) && (a as any[]).length > 0) as any[] | undefined;
    if (nonEmpty) return nonEmpty;
    if (Array.isArray(c)) return c;
    if (Array.isArray(it)) return it;
    if (Array.isArray(fd)) return fd;
    if (Array.isArray(el)) return el;
    return [];
}

/**
 * Source-of-truth lookup for the field tree on a checklist doc. The editor's
 * `resolveFieldsFromProcessOrChecklistRow` checks three paths because legacy
 * definitions stored the tree under data.fields / data.checklist.fields
 * instead of the top-level `fields` array. Mirror it so a cloned instance
 * lifted from any of those shapes still renders.
 */
export function resolveChecklistFields(instance: any): any[] {
    if (!instance) return [];

    // Erste Stelle, an der wirklich Felder liegen — top-level zuerst, dann die beiden Altlagen.
    //
    // Das stand als `(Array.isArray(a) && a.length > 0 && a) ?? (…) ?? (…)` da, und das kann nicht
    // funktionieren: Ein fehlgeschlagenes `&&` ergibt `false`, nicht `undefined`, und `??` läuft
    // nur bei `null`/`undefined` weiter. Der erste Ausdruck lieferte also `false`, `false ?? x`
    // ist `false`, und beide Alt-Zweige waren unerreichbar. Für eine ältere Instanz hieß das:
    // keine Felder, also verschwindet die Checkliste beim Speichern, und
    // `isChecklistInstanceComplete` meldet sie mangels Feldern als vollständig.
    const candidates = [
        instance.fields,
        instance?.data?.fields,
        instance?.data?.checklist?.fields,
    ];
    const populated = candidates.find(
        (candidate) => Array.isArray(candidate) && candidate.length > 0,
    );

    return populated ?? [];
}

/**
 * Updates one flattened value slot while preserving legacy field definitions.
 *
 * Some older checklist instances keep field definitions under `data.fields` or
 * `data.checklist.fields` instead of top-level `fields`. If we rewrite
 * `data` from object -> array without promoting the definitions, subsequent
 * renders can't resolve any fields and the checklist disappears.
 */
export function applyChecklistValueUpdate(
    prev: any,
    flatIndex: number,
    value: unknown,
): any {
    const base = prev && typeof prev === 'object' ? prev : {};
    const data = Array.isArray(base?.data) ? [...base.data] : [];
    while (data.length <= flatIndex) data.push(undefined);
    data[flatIndex] = value;

    // Preserve legacy field definitions when normalizing `data` to value-array.
    if (Array.isArray(base?.fields) && base.fields.length > 0) {
        return { ...base, data };
    }
    const resolvedFields = resolveChecklistFields(base);
    if (resolvedFields.length > 0) {
        return { ...base, fields: resolvedFields, data };
    }
    return { ...base, data };
}

type FlattenedField = { field: any; flatIndex: number; depth: number };

function flattenInstanceFields(fields: any[]): FlattenedField[] {
    const result: FlattenedField[] = [];
    const walk = (list: any[], depth: number) => {
        (list || []).forEach((f) => {
            result.push({ field: f, flatIndex: result.length, depth });
            if (isContainerField(f) && childrenOfField(f).length) {
                walk(childrenOfField(f), depth + 1);
            }
        });
    };
    walk(fields, 0);
    return result;
}

/**
 * Default value per field type (mirrors ChecklistInstanceForm's getValue
 * fallback). Used both for display defaults and completion checks.
 */
function defaultValueForType(type: string | undefined): unknown {
    const typeNorm = normalizeFieldType(type);
    if (typeNorm === 'oknotok' || typeNorm === 'donenotdone') return false;
    if (typeNorm === 'rating') return 0;
    return '';
}

function getValueOrTypeDefault(
    data: unknown,
    flatIndex: number,
    type: string | undefined,
): unknown {
    const values = Array.isArray(data) ? data : [];
    return values[flatIndex] !== undefined
        ? values[flatIndex]
        : defaultValueForType(type);
}

function getRawValueAtIndex(data: unknown, flatIndex: number): unknown {
    const values = Array.isArray(data) ? data : [];
    return values[flatIndex];
}

function hasValue(value: unknown, type: string | undefined): boolean {
    // Containers themselves never carry a value.
    if (canonicalContainerType(type) !== undefined) return true;
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    // Numeric fields (integer/decimal/rating) allow 0 as a valid explicit answer.
    if (typeof value === 'number') return Number.isFinite(value);
    // For binary toggles (OK/Not OK, Done/Not Done), false is an explicit
    // answer and should count as filled just like true.
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
    return true;
}

function isCompletableLeafField(field: any): boolean {
    const typeNorm = normalizeFieldType(fieldKind(field));
    return typeNorm !== undefined && SUPPORTED_LEAF_TYPES.has(typeNorm);
}

/**
 * Completion contract: every completable leaf field with `required: true`
 * has a value. Leaves without a chat input (unsupported types) are skipped
 * so they don't block completion. If the checklist's authors haven't marked
 * anything required (the editor doesn't surface a required toggle yet), fall
 * back to "every completable leaf has a value" so the runner doesn't enable
 * submit on an empty form.
 */
export function isChecklistInstanceComplete(checklist: any): boolean {
    const flat = flattenInstanceFields(resolveChecklistFields(checklist));
    const data = Array.isArray(checklist?.data) ? checklist.data : [];
    const completableLeaves = flat.filter(
        ({ field }) => !isContainerField(field) && isCompletableLeafField(field),
    );
    if (completableLeaves.length === 0) return true;
    const anyRequired = completableLeaves.some(({ field }) => field?.required === true);
    if (anyRequired) {
        return completableLeaves
            .filter(({ field }) => field?.required === true)
            .every(({ field, flatIndex }) => {
                const type = fieldKind(field);
                const value = getRawValueAtIndex(data, flatIndex);
                return hasValue(value, type);
            });
    }
    return completableLeaves.every(({ field, flatIndex }) => {
        const type = fieldKind(field);
        const value = getRawValueAtIndex(data, flatIndex);
        return hasValue(value, type);
    });
}

/**
 * Signature pad leaf renderer. Captures the stroke into a base64-encoded
 * PNG data URL and writes it back through `onChange` on every stroke end —
 * matches the "value is a string" convention so the existing completion
 * check (`hasValue` on string → length > 0) handles it for free.
 *
 * Read-only mode displays the captured PNG as an <img>.
 */
function SignatureField(props: {
    value: string | undefined;
    onChange: (next: string | undefined) => void;
    readOnly: boolean;
    clearLabel: string;
}) {
    const { value, onChange, readOnly, clearLabel } = props;
    const ref = useRef<SignatureCanvas | null>(null);

    // Restore a previously captured signature on mount / when the value
    // changes externally (instance swap). SignatureCanvas exposes fromDataURL
    // for this; the canvas must be mounted, so we trigger after a microtask.
    useEffect(() => {
        if (!value || !ref.current) return;
        try {
            ref.current.fromDataURL(value);
        } catch {
            /* malformed data URL — leave the canvas empty */
        }
    }, [value]);

    if (readOnly) {
        if (!value) {
            return <span className="text-sm text-muted-foreground">—</span>;
        }
        return (
            <img
                src={value}
                alt="signature"
                className="max-h-20 max-w-[220px] rounded-sm border bg-background"
            />
        );
    }

    return (
        <div className="flex flex-col gap-1">
            <div className="h-20 w-[220px] rounded-sm border bg-background">
                <SignatureCanvas
                    ref={(c: SignatureCanvas | null) => {
                        ref.current = c;
                    }}
                    canvasProps={{ width: 220, height: 80, style: { display: 'block' } }}
                    // Tintenfarbe der Unterschrift — sie landet als Pixel im
                    // exportierten PNG und ist deshalb ein fachlicher Wert,
                    // kein Oberflächen-Token.
                    penColor="#000"
                    onEnd={() => {
                        const pad = ref.current;
                        if (!pad) return;
                        if (pad.isEmpty()) {
                            onChange(undefined);
                            return;
                        }
                        onChange(pad.toDataURL('image/png'));
                    }}
                />
            </div>
            <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="chat-checklist-signature-clear"
                onClick={() => {
                    ref.current?.clear();
                    onChange(undefined);
                }}
            >
                {clearLabel}
            </Button>
        </div>
    );
}

/**
 * Sternebewertung — ersetzt den Fluent-`Rating`-Shim eins zu eins (gleiche
 * Sternzahl, gleiche Klickbarkeit, Null Sterne erlaubt). Die goldene Füllung
 * bekommt eine Kontur im warning-Token, damit der Stern die 3:1 für
 * nicht-textliche Elemente erreicht.
 */
function StarRating(properties: {
    value: number;
    max: number;
    readOnly?: boolean;
    label: string;
    onChange?: (value: number) => void;
}) {
    const stars = Array.from({ length: properties.max }, (_, index) => index);

    if (properties.readOnly) {
        return (
            <div
                role="img"
                aria-label={properties.label}
                data-testid="chat-checklist-rating"
                className="inline-flex items-center gap-0.5"
            >
                {stars.map((index) => (
                    <Star
                        key={index}
                        aria-hidden
                        className={cn(
                            'h-4 w-4',
                            index < properties.value ? 'fill-[#eab308] text-warning' : 'text-muted-foreground',
                        )}
                    />
                ))}
            </div>
        );
    }

    return (
        <div
            role="slider"
            aria-label={properties.label}
            aria-valuemin={0}
            aria-valuemax={properties.max}
            aria-valuenow={properties.value}
            data-testid="chat-checklist-rating"
            className="inline-flex items-center gap-0.5"
        >
            {stars.map((index) => (
                <button
                    key={index}
                    type="button"
                    aria-label={String(index + 1)}
                    // Touch-Polster bleibt bei 40 px, damit die fünf Sterne in
                    // die 220-px-Wertespalte passen (kein Querbalken im Chat).
                    className="rounded-sm p-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:p-3"
                    onClick={() => properties.onChange?.(index + 1)}
                >
                    <Star
                        aria-hidden
                        className={cn(
                            'h-4 w-4',
                            index < properties.value ? 'fill-[#eab308] text-warning' : 'text-muted-foreground',
                        )}
                    />
                </button>
            ))}
        </div>
    );
}

// ---- component ----

type Props = {
    instance: any;
    readOnly?: boolean;
    onChange?: (next: {
        checklist: any;
        isComplete: boolean;
        workItemId?: string | null;
        hasLocalEdits: boolean;
    }) => void;
};

export function ChecklistRunner({ instance, readOnly = false, onChange }: Props) {
    const { t } = useTranslation();
    const [checklist, setChecklist] = useState<any>(instance);
    const hasLocalEditsRef = useRef(false);
    const hasEmittedInitialChangeRef = useRef(false);

    // Reset when the parent swaps in a different instance.
    const instanceIdRef = useRef<string | null>(instance?._id ?? null);
    useEffect(() => {
        const nextId = instance?._id ?? null;
        if (nextId !== instanceIdRef.current) {
            instanceIdRef.current = nextId;
            hasLocalEditsRef.current = false;
            hasEmittedInitialChangeRef.current = false;
            setChecklist(instance);
        }
    }, [instance]);

    const fieldsForRender = useMemo(() => resolveChecklistFields(checklist), [checklist]);
    const flattened = useMemo(() => flattenInstanceFields(fieldsForRender), [fieldsForRender]);
    const isComplete = useMemo(() => isChecklistInstanceComplete(checklist), [checklist]);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(() => {
        const hasLocalEdits = hasLocalEditsRef.current;
        if (!hasLocalEdits && hasEmittedInitialChangeRef.current) return;
        hasEmittedInitialChangeRef.current = true;
        const workItemId = checklist?.bpmWorkItemId != null ? String(checklist.bpmWorkItemId) : null;
        onChangeRef.current?.({ checklist, isComplete, workItemId, hasLocalEdits });
    }, [checklist, isComplete]);

    const getValue = useCallback(
        (flatIndex: number): unknown => {
            return getValueOrTypeDefault(
                checklist?.data,
                flatIndex,
                fieldKind(flattened[flatIndex]?.field),
            );
        },
        [checklist?.data, flattened],
    );

    const setValue = useCallback(
        (flatIndex: number, value: unknown) => {
            if (readOnly) return;
            hasLocalEditsRef.current = true;
            setChecklist((prev: any) => applyChecklistValueUpdate(prev, flatIndex, value));
        },
        [readOnly],
    );

    const renderLeaf = (field: any, flatIndex: number): React.ReactNode => {
        const value = getValue(flatIndex);
        const required = field?.required === true;
        // Feldbeschriftung steht in der linken Spalte; Schalter und Sterne
        // tragen sie zusätzlich als zugänglichen Namen.
        const fieldLabel = String(field?.text ?? field?.label ?? field?.name ?? '');
        // Case-insensitive type match — definitions occasionally come back
        // with PascalCase variants (Signature, Photo, …) from older payloads.
        const typeNorm = normalizeFieldType(fieldKind(field)) ?? '';
        switch (typeNorm) {
            case 'text':
            case 'string':
                return field?.multiline === true ? (
                    <Textarea
                        data-testid="chat-checklist-input"
                        aria-label={fieldLabel || undefined}
                        className="resize-none"
                        rows={3}
                        value={(value as string) ?? ''}
                        onChange={event => setValue(flatIndex, event.target.value)}
                        disabled={readOnly}
                    />
                ) : (
                    <Input
                        data-testid="chat-checklist-input"
                        aria-label={fieldLabel || undefined}
                        value={(value as string) ?? ''}
                        onChange={event => setValue(flatIndex, event.target.value)}
                        disabled={readOnly}
                    />
                );
            case 'integer':
                return (
                    <Input
                        data-testid="chat-checklist-input"
                        aria-label={fieldLabel || undefined}
                        type="number"
                        value={value == null || value === '' ? '' : String(value)}
                        onChange={event =>
                            setValue(
                                flatIndex,
                                event.target.value === '' ? undefined : Number(event.target.value),
                            )
                        }
                        disabled={readOnly}
                    />
                );
            case 'decimal':
                return (
                    <Input
                        data-testid="chat-checklist-input"
                        aria-label={fieldLabel || undefined}
                        value={value == null || value === '' ? '' : String(value)}
                        onChange={event =>
                            setValue(
                                flatIndex,
                                event.target.value === '' ? undefined : parseFloat(event.target.value),
                            )
                        }
                        disabled={readOnly}
                    />
                );
            case 'oknotok':
                return (
                    <div className="inline-flex items-center gap-2">
                        <Switch
                            data-testid="chat-checklist-switch"
                            aria-label={fieldLabel || undefined}
                            checked={!!value}
                            onCheckedChange={next => setValue(flatIndex, next)}
                            disabled={readOnly}
                        />
                        <span className="text-sm leading-none">{value ? 'OK' : 'Not OK'}</span>
                    </div>
                );
            case 'donenotdone':
                return (
                    <div className="inline-flex items-center gap-2">
                        <Switch
                            data-testid="chat-checklist-switch"
                            aria-label={fieldLabel || undefined}
                            checked={!!value}
                            onCheckedChange={next => setValue(flatIndex, next)}
                            disabled={readOnly}
                        />
                        <span className="text-sm leading-none">{value ? 'Done' : 'Not Done'}</span>
                    </div>
                );
            case 'rating':
                return (
                    <StarRating
                        value={Number(value) || 0}
                        max={5}
                        readOnly={readOnly}
                        label={fieldLabel}
                        onChange={next => setValue(flatIndex, next)}
                    />
                );
            case 'signature':
                return (
                    <SignatureField
                        value={typeof value === 'string' ? value : undefined}
                        onChange={(next) => setValue(flatIndex, next)}
                        readOnly={readOnly}
                        clearLabel={t('checklist.signatureClear', { defaultValue: 'Clear' })}
                    />
                );
            default:
                return (
                    <span className="text-sm text-muted-foreground">
                        {t('checklist.unsupportedType', {
                            defaultValue: 'Field type "{{type}}" not yet supported in chat input.',
                            type: String(field?.type ?? '(unknown)'),
                        })}
                        {required ? ' *' : ''}
                    </span>
                );
        }
    };

    if (flattened.length === 0) {
        return (
            <div className="p-2 text-sm text-muted-foreground">
                {t('checklist.emptyChecklist', { defaultValue: 'Checklist is empty.' })}
            </div>
        );
    }

    // A checklist that serves several duties says which (tri-server checklistMerge): one appointment, one
    // sheet, and the proof for each of them. Who may do it is the most demanding of their performers.
    const served: any[] = Array.isArray(checklist?.servedObligations) ? checklist.servedObligations : [];

    return (
        <div className="max-h-[360px] overflow-y-auto pr-1">
            {served.length > 1 && (
                <div className="mb-2 rounded-md border bg-muted/40 p-2 text-xs" data-testid="checklist-served-obligations">
                    <div className="mb-1 font-semibold">
                        {t('checklist.servesObligations', { defaultValue: 'This checklist serves these duties:' })}
                    </div>
                    <ul className="list-disc space-y-0.5 pl-4">
                        {served.map((duty: any) => (
                            <li key={`${duty.obligationKey}@${duty.version}`} className="break-words">
                                <span className="font-medium">{[duty.regulation, duty.provisionId].filter(Boolean).join(' ')}</span>
                                {duty.description ? ` — ${duty.description}` : ''}
                            </li>
                        ))}
                    </ul>
                    {checklist?.requiredPerformer && (
                        <div className="mt-1 text-muted-foreground">
                            {t('checklist.requiredPerformer', { defaultValue: 'Performed by' })}:{' '}
                            {t(`checklist.performer.${checklist.requiredPerformer}`, { defaultValue: checklist.requiredPerformer })}
                        </div>
                    )}
                </div>
            )}
            {flattened.map(({ field, flatIndex, depth }) => {
                const text = field?.text ?? field?.label ?? field?.name ?? '';
                const isContainer = isContainerField(field);
                const required = field?.required === true;
                return (
                    <div
                        key={flatIndex}
                        data-testid="chat-checklist-row"
                        className="mb-1.5 flex items-start gap-2"
                        // Einrückungstiefe ist ein Laufzeitwert je Zeile.
                        style={{ paddingLeft: depth * 16 }}
                    >
                        <div className="min-w-0 grow">
                            {isContainer ? (
                                <div className="text-sm" title={text}>
                                    {text || '(group)'}
                                    {required ? ' *' : ''}
                                </div>
                            ) : (
                                <>
                                    <div className="text-sm font-semibold" title={text}>
                                        {text || '(unnamed)'}
                                        {required ? ' *' : ''}
                                    </div>
                                    {/* Where a step comes from when it is not the lead duty's own — a further duty or the
                                        manufacturer's document („Herstellerunterlage: …, S. 11“). */}
                                    {field?.normbezug && (
                                        <div className="text-xs text-muted-foreground break-words">{field.normbezug}</div>
                                    )}
                                    {/* The wording of the source the step was rewritten from (tri-server checklistInstructions). */}
                                    {field?.sourceText && field.sourceText !== text && (
                                        <div className="text-xs italic text-muted-foreground break-words">„{field.sourceText}“</div>
                                    )}
                                </>
                            )}
                        </div>
                        {!isContainer && (
                            <div className="w-[220px] shrink-0">
                                {renderLeaf(field, flatIndex)}
                            </div>
                        )}
                    </div>
                );
            })}
            {!readOnly && !isComplete && (
                <div className="mt-1.5 text-sm text-destructive">
                    {t('checklist.notCompleteHint', {
                        defaultValue: 'Fill all required (*) fields to enable completion.',
                    })}
                </div>
            )}
        </div>
    );
}
